import React, { useState, useCallback, useRef, useMemo, FunctionComponent, useEffect } from 'react';

// Next
// import Router from 'next/router';
import { useRouter } from 'next/router';
import { NextSeoProps } from 'next-seo';
import { GetServerSideProps } from 'next';

// Libs
import { useHttpContext, getHttpErrorMessage } from '@Libs/http';
import { getServerSideHttp as getServiceSideCMSHttp } from '@Libs/http/cms';

// API
import newsFeedsdApi from '@Libs/api/cms/newsFeed';

// GA
import { trackingEvent } from '@Components/Utils/Lazy/Script';

// Libs
import classnames from 'classnames';

// @Components
import Masonry from '@Components/UI/Masonry';
import Anchor from '@Components/Utils/Anchor';
import Slider, { SlickArrow } from '@Components/Utils/Slider';
import { useLayoutContext } from '@Components/Layout/Contexts';
import { ViewMedia, useViewDetected } from '@Components/Utils/ViewDetected';

// Contexts
import { useAppStore } from '@Components/AppStore/Context';
import { useAlertContext } from '@Components/Alert/Context';

// Component
import Empty from './Components/Empty';
import Layout from '@Pages/hoi-dap/Components/UI/Layout';
import AnswerCard from '@Pages/hoi-dap/Components/UI/AnswerCard';
import FormQuestion from '@Pages/hoi-dap/Components/Asking/FormQuestion';

// Styles
import styles from './Lists.module.scss';

// Types
import NewsFeedList from 'types/NewsFeedList';
import FeaturedQuestion, { Newsfeed } from 'types/FeaturedQuestion';

// Constant
const LIMIT = 10;
interface AnswerProps {
    tags: string[];
    keyword: string;
    listsTags: string[];
    newsfeeds: Array<NewsFeedList>;
    featuredLists: FeaturedQuestion;
}

import useSWR from 'swr';

const Answer: FunctionComponent<AnswerProps> = ({ newsfeeds, listsTags, featuredLists, keyword, tags }) => {
    const { alert } = useAlertContext();
    const { setLoading } = useAppStore();
    const { onScroll } = useLayoutContext();

    const { cmsHttp } = useHttpContext();
    const { deviceType } = useViewDetected();

    const router = useRouter();

    // Declare
    let checkFeaturedQuestion = true;
    const isMobile = deviceType !== 'desktop';

    // classnames
    const wrapperClassName = classnames([styles.wrapper, { [styles.mobile]: isMobile }]);

    //useRef
    const slider = useRef<any>(null);

    // useState
    const [hasLoadMore, setHasLoadMore] = useState<boolean>(false);
    const [newsFeedLists, setNewsFeedLists] = useState<Array<NewsFeedList>>([]);

    //useEffect

    useEffect(() => {
        setHasLoadMore(newsfeeds?.length < LIMIT);
        setNewsFeedLists(newsfeeds);
    }, [keyword, tags]);

    const { data, mutate } = useSWR(router?.asPath, { initialData: newsfeeds });

    useEffect(() => {
        if (sessionStorage.getItem('hoiDap')) {
            const scrollPos = JSON.parse(sessionStorage?.getItem('hoiDap'));
            if (scrollPos) {
                if (JSON.stringify(newsFeedLists ?? newsfeeds) !== JSON.stringify(data)) {
                    setNewsFeedLists(data);
                }
            }
        }
    }, []);

    useEffect(() => {
        const handleWindowClose = (e) => {
            e.preventDefault();
            if (sessionStorage.getItem('hoiDap')) {
                sessionStorage.removeItem('hoiDap');
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
        };
        window.addEventListener('beforeunload', handleWindowClose);
        return () => {
            window.removeEventListener('beforeunload', handleWindowClose);
        };
    }, []);

    // Setting
    const sliderSettings = {
        speed: 200,
        dots: false,
        swipe: true,
        arrows: false,
        slidesToScroll: 1,
        swipeToSlide: true,

        autoplaySpeed: 3000,
        slidesToShow: { desktop: 3, tablet: 2, mobile: 1 }[deviceType],
        infinite: isMobile || [...Array(4)].length >= 3,
        ...(isMobile && { centerMode: true, centerPadding: '48px' }),
        className:
            [...Array(4)].length >= 3 ? styles.views_slider : classnames([styles.views_slider, styles.slider_less]),
    };

    // check page empty
    const checkEmpty = useMemo(() => {
        return newsFeedLists?.length === 0 || false;
    }, [newsFeedLists]);

    // scrolls
    onScroll(
        (event, { isBottom }) => {
            if (isBottom) loadMore();
        },
        [newsFeedLists?.length],
    );

    // Load More
    const loadMore = useCallback(async () => {
        if (hasLoadMore) return;

        try {
            setLoading(true);

            const { newsfeeds } = await newsFeedsdApi(cmsHttp).getListsNewsFeeds({
                keyword,
                tags,
                startIndex: newsFeedLists?.length,
            });
            setHasLoadMore(newsfeeds?.length < LIMIT);

            const newData = [...newsFeedLists, ...newsfeeds];

            // event tracking
            trackingEvent('nhấp vào "Xem thêm"', 'List câu hỏi');

            // add data
            setNewsFeedLists(newData);
            mutate([...newData], false);
        } catch (error) {
            alert({
                type: 'error',
                title: 'Đã xảy ra lỗi',
                text: getHttpErrorMessage(error),
            });
        } finally {
            setLoading(false);
        }
    }, [keyword, tags, hasLoadMore, newsFeedLists?.length]);

    if (tags?.length > 0 || keyword !== null) checkFeaturedQuestion = false;

    const [featuredFirst, ...newsFeedLastLists]: Array<NewsFeedList> = newsFeedLists || [];
    const listsNewsfeeds = checkFeaturedQuestion ? newsFeedLastLists : newsFeedLists || [];

    // Câu hỏi nổi bật (1 item)
    const renderNewsFeatured = () => {
        const attachmentImages = featuredFirst?.attachment?.url;
        const doctor = featuredFirst?.question?.questionComment?.doctor;

        return (
            <Anchor href={`/hoi-dap/${featuredFirst?.slug}`} key={featuredFirst?.slug} className={styles.anchor}>
                <AnswerCard
                    imagePosition="left"
                    doctorProfile={doctor}
                    image={attachmentImages}
                    imgClassName={styles.image}
                    title={featuredFirst?.title}
                    className={styles.answer_new}
                />
            </Anchor>
        );
    };

    // Danh sách câu hỏi nổi bật
    const renderFeaturedQuestion = (newsFeeds) => {
        return newsFeeds?.map((newsFeed: Newsfeed) => {
            // Declare
            const slug = newsFeed?.slug;
            const attachmentImages = newsFeed?.attachment?.url;
            const doctor = newsFeed?.question?.questionComment?.doctor;

            return (
                <Anchor key={`featured_question_${slug}`} href={`/hoi-dap/${slug}`} className={styles.anchor}>
                    <AnswerCard
                        title={newsFeed?.title}
                        doctorProfile={doctor}
                        className={styles.view}
                        answerClassName={styles.title}
                        imgClassName={styles.view_img}
                        image={attachmentImages || '/assets/default_img.png'}
                    />
                </Anchor>
            );
        });
    };

    // Danh sách câu hỏi nổi bật

    const renderNewsFeeds = (newsFeeds) => {
        return newsFeeds.map((newsFeed: NewsFeedList) => {
            // Declare
            const slug = newsFeed?.slug;
            const attachmentImages = newsFeed?.attachment?.url;
            const doctor = newsFeed?.question?.questionComment?.doctor;

            return (
                <Anchor href={`/hoi-dap/${slug}`} className={styles.card_item} key={`lists_news_feed_${slug}`}>
                    <AnswerCard
                        doctorProfile={doctor}
                        image={attachmentImages}
                        className={styles.card_item}
                        content={newsFeed?.question?.content}
                        title={attachmentImages ? newsFeed?.title : ''}
                    />
                </Anchor>
            );
        });
    };

    return (
        <Layout {...{ listsTags, tags }}>
            <ViewMedia is={['desktop']}>
                <FormQuestion active={false} />
            </ViewMedia>
            <div className={wrapperClassName}>
                {checkFeaturedQuestion && renderNewsFeatured()}

                {checkFeaturedQuestion && (
                    <div className={styles.hot_answer_wrapper}>
                        <div className={styles.top_view}>
                            <div className={styles.title}>Câu hỏi nổi bật</div>
                            <ViewMedia is="desktop">
                                <div className={styles.btn_control}>
                                    <SlickArrow
                                        width={24}
                                        height={24}
                                        direction="prev"
                                        className={styles.icon_row}
                                        arrowSrc="/assets/bai-viet/ic_row.png"
                                        onClick={() => slider.current.slickPrev(0)}
                                    />
                                    <SlickArrow
                                        width={24}
                                        height={24}
                                        direction="next"
                                        className={styles.icon_row}
                                        arrowSrc="/assets/bai-viet/ic_row.png"
                                        onClick={() => slider.current.slickNext(0)}
                                    />
                                </div>
                            </ViewMedia>
                        </div>
                        <div className={styles.views}>
                            <Slider {...sliderSettings} ref={slider}>
                                {renderFeaturedQuestion(featuredLists)}
                            </Slider>
                        </div>
                    </div>
                )}

                <Masonry colsNumber={isMobile ? 1 : 2} className={styles.masonry} columnClassName={styles.col}>
                    {listsNewsfeeds?.length > 0 && renderNewsFeeds(listsNewsfeeds)}
                </Masonry>
                {checkEmpty && <Empty />}
            </div>
        </Layout>
    );
};

export default Answer;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const cmsHttp = getServiceSideCMSHttp(ctx);

    // Declare
    const keyword: any = ctx?.query?.s || null;
    const getTags: any = ctx?.query?.pho_bien || [];
    const tags = Array.isArray(getTags) ? getTags : [getTags];

    // lấy danh sách câu hỏi
    const { newsfeeds = [] } = await newsFeedsdApi(cmsHttp).getListsNewsFeeds({
        ...(keyword && { keyword }),
        ...(tags && { tags }),
    });

    // lấy danh sách câu hỏi nổi bật
    const { newsfeeds: featuredLists = [], tags: listsTags = [] } = await newsFeedsdApi(cmsHttp).getFeatured();

    const seo: NextSeoProps = {
        title: 'eDoctor: Cộng đồng',
    };

    return {
        props: {
            seo,
            tags,
            keyword,
            listsTags,
            newsfeeds,
            featuredLists,
        },
    };
};
